import { normalizeWorkspaceMode } from './accountCenterUtils';
import { buildImageSpaceCreatorPayload } from '@/utils/imageSpaceCreatorMeta';
import { getIpeWorkspaceMode, fundingAuthAPI } from '@/utils/api';

export function resolveListingRecordWorkspace(record) {
  const raw = String(record?.workspaceMode || record?.captureSpace || '').trim().toLowerCase();
  if (raw === 'team' || raw === 'personal') return raw;
  const teamId = record?.teamId ?? record?.team_id;
  if (teamId != null && teamId !== '') return 'team';
  return 'personal';
}

export function resolveListingScopeNo(record) {
  const explicit = String(record?.scopeNo || record?.scope_no || '').trim();
  if (explicit) return explicit;
  if (resolveListingRecordWorkspace(record) === 'team') {
    return String(record?.teamNo || record?.team_no || '').trim();
  }
  return String(record?.userNo || record?.user_no || '').trim();
}

export function buildListingWorkspaceScope(user, authUserId, workspaceMode, { teamId = null, teamNo = '' } = {}) {
  const mode = normalizeWorkspaceMode(workspaceMode ?? getIpeWorkspaceMode());
  const userNo = user?.userNo ? String(user.userNo).trim() : '';
  const normalizedTeamNo = teamNo ? String(teamNo).trim() : '';
  const scopeNo = mode === 'team' ? normalizedTeamNo : userNo;
  return {
    workspaceMode: mode,
    userNo,
    teamNo: mode === 'team' ? normalizedTeamNo : '',
    teamId: mode === 'team' ? teamId : null,
    scopeNo,
    authUserId,
  };
}

export function buildListingWorkspacePayload(user, authUserId, scope) {
  const workspaceScope = scope && typeof scope === 'object'
    ? scope
    : buildListingWorkspaceScope(user, authUserId, getIpeWorkspaceMode());
  const base = buildImageSpaceCreatorPayload(
    user,
    authUserId,
    workspaceScope.workspaceMode,
    workspaceScope.teamId,
  );
  const payload = {
    ...base,
    userNo: workspaceScope.userNo || undefined,
    teamNo: workspaceScope.teamNo || undefined,
    scopeNo: workspaceScope.scopeNo || undefined,
  };
  if (workspaceScope.workspaceMode === 'team' && workspaceScope.teamId != null) {
    payload.teamId = workspaceScope.teamId;
  }
  return payload;
}

function resolveRecordOwnerId(record) {
  const raw = record?.ownerUserId ?? record?.ownerId ?? record?.owner_id ?? record?.createdByUserId;
  return raw != null && raw !== '' ? String(raw) : '';
}

export function matchesListingWorkspaceScope(record, scope) {
  if (!scope) return true;
  // 官方商品模板对全体用户可见
  if (record?.official) return true;
  const mode = normalizeWorkspaceMode(scope.workspaceMode);
  const recordMode = resolveListingRecordWorkspace(record);
  if (recordMode !== mode) return false;

  if (mode === 'team') {
    const recordTeamId = record?.teamId ?? record?.team_id;
    if (scope.teamId != null && recordTeamId != null && String(recordTeamId) === String(scope.teamId)) {
      return true;
    }
    const recordScopeNo = resolveListingScopeNo(record);
    const currentScopeNo = String(scope.scopeNo || '').trim();
    if (recordScopeNo && currentScopeNo && recordScopeNo === currentScopeNo) {
      return true;
    }
    // 兼容仅写了 workspaceMode=team、尚未带上 teamId/编号 的历史数据（有明确团队标识时不得跨团队放行）
    const hasTeamIdentity = (recordTeamId != null && recordTeamId !== '') || Boolean(recordScopeNo);
    if (!hasTeamIdentity && recordMode === 'team' && scope.authUserId != null) {
      const recordOwner = resolveRecordOwnerId(record);
      if (recordOwner && recordOwner === String(scope.authUserId)) {
        return true;
      }
    }
    return false;
  }

  const recordScopeNo = resolveListingScopeNo(record);
  const currentScopeNo = String(scope.scopeNo || '').trim();
  if (recordScopeNo && currentScopeNo) {
    return recordScopeNo === currentScopeNo;
  }

  const recordOwner = resolveRecordOwnerId(record);
  return scope.authUserId != null && recordOwner !== ''
    && recordOwner === String(scope.authUserId);
}

export function filterByListingWorkspaceScope(records, scope) {
  const list = Array.isArray(records) ? records : [];
  if (!scope) return list;

  const mode = normalizeWorkspaceMode(scope.workspaceMode);
  if (!scope.ready) {
    // 团队编号尚未就绪时，先按工作区模式粗筛，避免列表整页空白
    return list.filter((record) => resolveListingRecordWorkspace(record) === mode);
  }

  return list.filter((record) => matchesListingWorkspaceScope(record, scope));
}

/** 保存套图/上架模板时解析当前工作区 scope（团队模式会拉取团队编号） */
export async function resolveListingWorkspaceScopeForSave(user, authUserId) {
  const workspaceMode = getIpeWorkspaceMode();
  if (workspaceMode !== 'team') {
    return buildListingWorkspaceScope(user, authUserId, workspaceMode, { teamId: null, teamNo: '' });
  }
  try {
    const overview = await fundingAuthAPI.getTeamOverview();
    const team = overview?.team && typeof overview.team === 'object' ? overview.team : overview;
    const teamId = team?.id ?? team?.teamId ?? null;
    const teamNo = team?.teamNo ?? team?.team_no ?? '';
    return buildListingWorkspaceScope(user, authUserId, workspaceMode, { teamId, teamNo });
  } catch {
    return buildListingWorkspaceScope(user, authUserId, workspaceMode, { teamId: null, teamNo: '' });
  }
}

/**
 * 生成套图模板 ID。
 * 使用日期 + 随机后缀，避免旧版「按当日序号 + getAll 分页」导致 ID 撞车，
 * 进而复用他人/软删残留的 template-images 记录，保存时 batch 404。
 */
export function generateImageTemplateId(_existingTemplates, _scope) {
  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const rand = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  return `IMG-${dateStr}-${rand}`;
}
